import { NavLink, Link, useLocation } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { sectionForPath } from '../data/adminSections'

/**
 * The tab bar for whichever admin section you are in, and the way back to the
 * hub. The section is derived from the route rather than passed as a prop:
 * eight pages would otherwise each have to declare which section they belong
 * to, and moving a page between sections would be two edits instead of one.
 */
export default function AdminNav() {
  const { pathname } = useLocation()
  const section = sectionForPath(pathname)
  if (!section) return null

  const Action = section.action?.icon

  return (
    <nav className="mb-6 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-4">
      <Link
        to="/admin"
        className="mr-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Admin
      </Link>
      <span className="mr-1 text-[0.65rem] font-semibold uppercase tracking-wider text-gray-400">
        {section.name}
      </span>

      {section.pages.map((page) => {
        // Destructured in the body rather than the parameter list: the lint
        // config only exempts unused *variables* starting with a capital, not
        // unused *arguments*, and a component read out of props trips it.
        const Icon = page.icon
        const { to, label, hint } = page
        return (
          <NavLink
            key={to}
            to={to}
            end
            title={hint}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-purple-100 text-purple-800'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        )
      })}

      {section.action && (
        <NavLink
          to={section.action.to}
          title={section.action.hint}
          className="ml-auto inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:from-indigo-500 hover:to-purple-500"
        >
          <Action className="h-4 w-4" />
          {section.action.label}
        </NavLink>
      )}
    </nav>
  )
}
